import Spinner from "@/components/ui/spinner";
import { track } from "@/features/analytics";
import type { IMediaFile } from "@/shared/types";
import { useGetMediaFoldersQuery } from "@api/media-folders";
import { useBulkUpdateMediaLibFilesMutation } from "@api/media-library";
import Button from "@components/ui/button";
import Icon from "@components/ui/icon";
import Popover from "@components/ui/popover";
import {
  MediaLibCategoryEnum,
  MediaLibDialogBase,
  useMediaLibDialogStoreShallow,
} from "@editor/modules/media-lib";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { MediaLibFileMoveDialogFolders } from "../../files/dialogs/media-lib-file-move-dialog/media-lib-file-move-dialog-folders";

const DIALOG_ID = "media-lib-dialog-bulk-files-move";

interface IProps {
  bulkUpdateFiles: (ids: string[], data: Partial<IMediaFile>) => void;
}

export const MediaLibDialogBulkFilesMoveDialog = ({
  bulkUpdateFiles,
}: IProps) => {
  const dispatch = useAppDispatch();

  const { items, setSelectedItems } = useMediaLibDialogStoreShallow((s) => ({
    items: s.selectedItems,
    setSelectedItems: s.setSelectedItems,
  }));

  const [updateFiles, { isLoading: isSaving }] =
    useBulkUpdateMediaLibFilesMutation();

  const { data: allFolders = [], isFetching: isLoadingFolders } =
    useGetMediaFoldersQuery({ archived: false });
  const isProcessing = isLoadingFolders || isSaving;

  const [selectedFolderId, setSelectedFolderId] = useState<null | string>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const selectedFolder = useMemo(() => {
    return allFolders.find(
      (el) =>
        el._id === selectedFolderId &&
        ![MediaLibCategoryEnum.Website, MediaLibCategoryEnum.Import].includes(
          el.parentId as MediaLibCategoryEnum,
        ),
    );
  }, [allFolders, selectedFolderId]);

  const handleSubmit = async () => {
    if (isProcessing || isLoadingFolders) return;

    try {
      const ids = items
        .filter((el) => el.mediaFrom === "my-library")
        .map((el) => el.id);
      if (ids.length === 0) return;

      const updateData = {
        archived: false,
        folderId:
          selectedFolderId === MediaLibCategoryEnum.AllFiles
            ? null
            : selectedFolderId,
      };

      await updateFiles({
        ids: ids,
        data: updateData,
      }).unwrap();
      bulkUpdateFiles(ids, updateData);
      setSelectedItems([]);
    } catch (e) {
      console.log(e);
      track("error moving media files to folder", {
        items,
        folderId: selectedFolder,
        error: e,
      });
      toast.error("Error moving files");
    }

    handleClose();
  };

  let folderTitle: string | undefined;
  if (selectedFolderId) {
    if (selectedFolderId === MediaLibCategoryEnum.BrandAssets) {
      folderTitle = "Brand assets";
    } else if (selectedFolderId === MediaLibCategoryEnum.AllFiles) {
      folderTitle = "All files";
    } else folderTitle = selectedFolder?.title;
  }

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header
        title="Move to a folder"
        onClose={handleClose}
      />
      <MediaLibDialogBase.Content>
        <div>
          <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
            Folder
          </span>
          <div className="tw-relative">
            {isLoadingFolders && (
              <span className="tw-absolute tw-inset-0 tw-z-1 tw-size-full tw-bg-white/50"></span>
            )}
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <Popover.Trigger>
                <div
                  className={cn(
                    "tw-relative tw-flex tw-h-10 tw-w-full tw-cursor-pointer tw-items-center tw-rounded-md tw-border tw-border-solid tw-border-blue-100 tw-px-4 tw-font-sans tw-text-[13px] tw-font-normal tw-text-blue",
                    isOpen &&
                      "tw-outline tw-outline-2 tw-outline-offset-2 tw-outline-[#7b17cf33]",
                  )}
                >
                  <span className={cn(!folderTitle && "tw-text-blue-500")}>
                    {folderTitle || "Select folder"}
                  </span>
                  <div className="tw-absolute tw-right-2 tw-top-1/2 tw-flex -tw-translate-y-1/2 tw-items-center">
                    {isLoadingFolders ? (
                      <Spinner />
                    ) : (
                      <Icon
                        icon="ri-arrow-down-s-fill"
                        className={cn(
                          "tw-ml-auto tw-text-xl/none tw-text-blue tw-transition-transform",
                          {
                            "tw-rotate-180": isOpen,
                          },
                        )}
                      />
                    )}
                  </div>
                </div>
              </Popover.Trigger>
              <Popover.Content
                data-sd-is-floating-popover
                className="tw-z-[99999] tw-w-[464px] tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white"
              >
                <MediaLibFileMoveDialogFolders
                  showAllFiles={true}
                  selectedFolderId={selectedFolderId}
                  setSelectedFolderId={setSelectedFolderId}
                  setIsOpen={setIsOpen}
                />
              </Popover.Content>
            </Popover>
          </div>
        </div>
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          disabled={!selectedFolderId}
          className={cn(isProcessing && "tw-cursor-not-allowed")}
          onClick={handleSubmit}
        >
          {isProcessing ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Moving...
            </>
          ) : (
            "Move"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibDialogBulkFilesMoveDialog.dialogId = DIALOG_ID;
